import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApolloClient, useMutation } from '@apollo/client'
import { Asset } from '@/organisms/AngelPlayer'
import {
  createVideoTrackEvent,
  PifAttributionEventName,
  TrackVideoEventName,
} from '@/organisms/AngelPlayer/AngelPlayerContext/trackEventFactory'
import { useGuildMember } from '@/services/GuildUserService'
import { isAngelBehindTheScenes } from '@/utils/SpecialProjectUtils'
import { useSafeTrack } from '@/utils/analytics'
import {
  ApplyAttributionResponse,
  Benefactor,
  createPifAttributionMutation,
} from '@/views/WatchEpisodeView/ShowAttribution'

interface AttributionPlayerValues {
  showThankYouModal: boolean
  handleModalCancel: () => void
  handleModalSave: () => void
  shouldShowAttribution: boolean
  fullscreenAttributionClasses: string
  handleThankYouClick: () => void
  benefactor?: Benefactor
  loading: boolean
}

interface UseAttributionPlayerProps {
  asset?: Asset
  skip?: boolean
  client?: ApolloClient<object>
}

export const useAttributionPlayer = ({ asset, skip }: UseAttributionPlayerProps): AttributionPlayerValues => {
  const assetProjectSlug = asset?.projectSlug
  const assetEpisodeSlug = asset?.sourceConfig?.slug
  const track = useSafeTrack()
  const [fullscreenAttributionClasses, setFullscreenAttributionClasses] = useState('')
  const [showThankYouModal, setShowThankYouModal] = useState(false)
  const shouldShowAttribution = !skip && !isAngelBehindTheScenes(assetProjectSlug as string) && !!asset
  const [benefactor, setBenefactor] = useState<Benefactor>()
  const { loading: isLoadingGuildUser, isGuildMember } = useGuildMember()
  const timeoutRef = useRef<NodeJS.Timeout>()

  const [createPifAttribution, { loading }] = useMutation<ApplyAttributionResponse>(createPifAttributionMutation, {
    errorPolicy: 'all',
  })

  useEffect(() => {
    if (shouldShowAttribution && assetProjectSlug && assetEpisodeSlug && !isLoadingGuildUser && !isGuildMember) {
      createPifAttribution({
        variables: { input: { projectSlug: assetProjectSlug, episode: assetEpisodeSlug, record: true } },
      }).then((response) => {
        setBenefactor(response.data?.createPifAttribution?.benefactorAttributionRollup)
        setFullscreenAttributionClasses('')
        timeoutRef.current = setTimeout(() => {
          setFullscreenAttributionClasses('hidden')
        }, 6000)
      })
    }

    return () => {
      clearTimeout(timeoutRef.current as NodeJS.Timeout)
    }
  }, [
    createPifAttribution,
    shouldShowAttribution,
    assetProjectSlug,
    assetEpisodeSlug,
    isGuildMember,
    isLoadingGuildUser,
  ])

  const sendPifTrackEvent = useCallback(
    (eventName: TrackVideoEventName) => {
      if (benefactor && asset) {
        track(eventName, {
          ...createVideoTrackEvent(eventName, asset),
          attributed_by: {
            region: benefactor?.country,
            user_displayed_name: benefactor?.firstName,
          },
        })
      }
    },
    [benefactor, asset, track],
  )

  const handleThankYouClick = useCallback(() => {
    setShowThankYouModal(true)
    sendPifTrackEvent(PifAttributionEventName.PifThankYouStarted)
  }, [setShowThankYouModal, sendPifTrackEvent])

  const handleModalSave = useCallback(() => {
    setShowThankYouModal(false)
    sendPifTrackEvent(PifAttributionEventName.PifThankYouCompleted)
  }, [sendPifTrackEvent])

  const handleModalCancel = useCallback(() => {
    setShowThankYouModal(false)
    sendPifTrackEvent(PifAttributionEventName.PifThankYouCancelled)
  }, [sendPifTrackEvent])

  return useMemo(() => {
    return {
      fullscreenAttributionClasses,
      showThankYouModal,
      shouldShowAttribution,
      handleModalCancel,
      handleModalSave,
      handleThankYouClick,
      benefactor,
      loading,
    }
  }, [
    benefactor,
    fullscreenAttributionClasses,
    handleModalCancel,
    handleModalSave,
    handleThankYouClick,
    loading,
    shouldShowAttribution,
    showThankYouModal,
  ])
}
